import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash-es";
import { type FC, useEffect, useRef } from "react";
import type { UUID } from "../../../utils/uuid";
import { RestrictedChip } from "../../moment/restricted-chip";
import { useToTarget } from "../../to/use-to-target";
import { useSegmentStateOptions } from "../hooks/use-segment-options";
import { useSegmentSelector } from "../hooks/use-segment-selector";

export const SegmentSelectAutocomplete: FC<{
  segmentSelectorInfo: ReturnType<typeof useSegmentSelector>;
  restrictedSegments: UUID[];
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  showConnectionHeading?: boolean;
}> = ({
  disabled,
  label = "Segment",
  placeholder = "Segment",
  restrictedSegments,
  segmentSelectorInfo,
  showConnectionHeading = true,
}) => {
  const { toTarget } = useToTarget();

  const options = useSegmentStateOptions();
  const oldOptions = useRef(options);

  useEffect(() => {
    if (
      options.length !== oldOptions.current.length &&
      segmentSelectorInfo.data?.selectedSegment?.id
    ) {
      oldOptions.current = options;
      void segmentSelectorInfo
        .selectSegment(segmentSelectorInfo.data.selectedSegment.id)
        .catch(console.error);
    }
  }, [options, segmentSelectorInfo]);

  const restrictedSelected =
    segmentSelectorInfo.data?.selectedSegment?.id &&
    restrictedSegments.includes(segmentSelectorInfo.data.selectedSegment.id);

  return (
    <FormControl fullWidth>
      {toTarget === "manager" ? <h3>Who do they manage?</h3> : null}
      {toTarget === "connection" && showConnectionHeading ? (
        <h3>Of people (subjects) in this segment</h3>
      ) : null}
      <Autocomplete
        id="segment-select"
        loading={segmentSelectorInfo.loading}
        value={options.find((s) => s.id === segmentSelectorInfo.data?.selectedSegment?.id) || null}
        options={options}
        renderOption={(renderProps, option) =>
          option.name === "New Segment" ? (
            <Box component="li" {...renderProps} key={option.id}>
              <Typography sx={{ color: "primary.main", fontWeight: "bold" }}>
                Create new segment
              </Typography>
            </Box>
          ) : (
            <Tooltip title={option.description} key={option.id}>
              <Box component="li" {...renderProps} key={option.id}>
                <Stack direction="row" spacing={1}>
                  <Typography>{option.name}</Typography>
                  {restrictedSegments.includes(option.id) && <RestrictedChip />}
                </Stack>
              </Box>
            </Tooltip>
          )
        }
        renderInput={(params) => (
          <TextField
            label={label}
            placeholder={placeholder}
            variant="outlined"
            {...params}
            helperText={
              restrictedSelected && (
                <Alert severity="warning">
                  You don’t have permission to publish to this segment. You can save the
                  Communication as a draft and have someone else publish, or choose a different
                  segment.
                </Alert>
              )
            }
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: restrictedSelected ? <RestrictedChip sx={{ mr: 1 }} /> : null,
                endAdornment: (
                  <>
                    {segmentSelectorInfo.loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                "data-testid": "segment-select",
              },
            }}
          />
        )}
        getOptionLabel={(option) => {
          if (!option.name) return "";
          return `${option.name}${segmentSelectorInfo.data?.isNew && segmentSelectorInfo.data?.selectedSegment?.name ? ` (${segmentSelectorInfo.data?.selectedSegment.name})` : ""}`;
        }}
        groupBy={(option) =>
          isEmpty(option.favorite_segment) ? "Existing Segments" : "⭐ Favorites"
        }
        onChange={(_, v) => {
          void segmentSelectorInfo.selectSegment(v?.id).catch(console.error);
        }}
        disabled={segmentSelectorInfo.loading || disabled}
      />
    </FormControl>
  );
};
